import React, { useState, useEffect } from 'react';
import http from './http';

const AUTH_TOKENS_KEY = 'lo-token-auth';
const STRICT_TOKEN_PREFIX = 'lo-strict-token';

let tokens;

// use if no localStorage available
let TOKENS_STORAGE = {};

const readAuthFromIFrame = async authIframeUrl => {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('src', `${authIframeUrl}/auth.html`);
  iframe.setAttribute('frameborder', '0px');
  iframe.style.width = '0px';
  iframe.style.height = '0px';
  document.body.appendChild(iframe);

  iframe.onload = function() {
    iframe.contentWindow.postMessage('get_tokens', authIframeUrl);
  };

  return new Promise(resolve => {
    function receiveMessage(event) {
      if (event.origin === authIframeUrl) {
        const { data } = event;
        if (typeof data === 'string' && !!data) {
          const { title, tokens } = JSON.parse(data);
          if (title === 'post_data') {
            resolve(tokens['lo-token-auth']);
            iframe.remove();
          }
        }
      }
    }
    window.addEventListener('message', receiveMessage);
  });
};

const clearStrictTokens = () => {
  Object.keys(localStorage).forEach(key => {
    if (key.indexOf(STRICT_TOKEN_PREFIX) >= 0) {
      localStorage.removeItem(key);
    }
  });
};

const readTokens = async ({ fallbackIframe } = {}) => {
  if (tokens) return tokens;

  let item;
  if (localStorage && localStorage.getItem) {
    item = localStorage.getItem(AUTH_TOKENS_KEY);
  }
  if (!localStorage) {
    item = TOKENS_STORAGE[AUTH_TOKENS_KEY];
  }
  if (!item && fallbackIframe) {
    item = await readAuthFromIFrame({ fallbackIframe });
  }
  if (!item) return null;

  tokens = JSON.parse(item);
  return tokens;
};

const extractToken = (payload, name) => {
  if (!payload) return null;
  const [token, expiresAt] = payload[name];
  if (expiresAt * 1000 < Date.now()) return null;
  return token;
};

const getRefreshToken = async opts => {
  const t = await readTokens(opts);
  return extractToken(t, 'refresh_token');
};

const getAuthToken = () => {
  const t = readTokens();
  return extractToken(t, 'auth_token');
};

const setTokens = newTokens => {
  tokens = newTokens;
  if (localStorage) {
    localStorage.setItem(AUTH_TOKENS_KEY, JSON.stringify(newTokens));
  } else {
    TOKENS_STORAGE = { [AUTH_TOKENS_KEY]: JSON.stringify(newTokens) };
  }

  return tokens;
};

const resetTokens = () => {
  if (localStorage) {
    localStorage.removeItem(AUTH_TOKENS_KEY);
    clearStrictTokens();
  } else {
    TOKENS_STORAGE = { [AUTH_TOKENS_KEY]: null };
  }
};

const logout = resetTokens;

const processResponse = async response => {
  const { errors } = response;

  if (errors) {
    return [false, errors];
  }

  setTokens(response);
  return [true, await getAccessToken()];
};

const login = async (fields, callback) => {
  const response = await http('POST', '/login', fields);
  if (callback(response)) processResponse(response);
};

const exchange = async (token, callback) => {
  const response = await http('POST', '/exchange', { token });
  if (callback(response)) processResponse(response);
};

const register = async fields => {
  const response = await http('POST', '/register', fields);
  const processed = await processResponse(response);
  return processed;
};

const join = async fields => {
  const response = await http('POST', '/join', fields);
  const processed = await processResponse(response);
  return processed;
};

const getAccessToken = async opts => {
  const t = await readTokens(opts);
  return extractToken(t, 'access_token');
};

const isLoggedIn = () => !!getRefreshToken();

const fetchAccessToken = async () => {
  const accessToken = await getAccessToken();

  if (accessToken) return [true, accessToken];

  const token = await getRefreshToken();
  if (!token) return [false, 'refresh token is empty or expired'];

  const response = await http('POST', '/refresh', { token });
  const processed = await processResponse(response);
  return processed;
};

const ProfileContext = React.createContext(null);

const authHeaders = async () => {
  const [ok, token] = await fetchAccessToken();
  if (ok) return { authorization: `Bearer ${token}` };
  return {};
};

const useAuthHeaders = () => {
  const [headers, setHeaders] = useState();

  const fetchHeaders = async () => setHeaders(await authHeaders());

  useEffect(() => {
    fetchHeaders();
  }, []);

  return headers;
};

const unsafeGetAccessToken = () => {
  // run refreshing tokens if needed
  fetchAccessToken();
  // trying to get current token
  // even if it empty now it will be refreshed on next iteration
  return getAccessToken();
};

export {
  unsafeGetAccessToken,
  getRefreshToken,
  login,
  logout,
  register,
  join,
  exchange,
  authHeaders,
  isLoggedIn,
  useAuthHeaders,
  ProfileContext,
  STRICT_TOKEN_PREFIX,
};
